import { useQuery } from '@tanstack/react-query';
import { Wallet } from 'lucide-react';
import { WalletSaveLink } from '@cloudsign/shared';
import { ApiClientError, api } from '../../lib/api';
import { useVisitorLanguage } from './locale';

/**
 * "Add to Google Wallet" on the pre-arrival page (docs/modules/wallet.md s4: GET /wallet/prearrival/:token/link, public,
 * checked like the page itself). 404 means the wallet is off for this organisation, so nothing is drawn. The link is a
 * Google address that opens in a new tab; we never log or show it.
 */
export function WalletButton({ token }: { token: string }) {
  const { vt } = useVisitorLanguage();
  const q = useQuery({
    queryKey: ['public-link', 'wallet', token],
    queryFn: () => api.get(`/wallet/prearrival/${encodeURIComponent(token)}/link`, { schema: WalletSaveLink, quiet401: true }),
    retry: false,
    staleTime: 5 * 60_000,
  });
  if (q.isPending) return null;
  if (q.isError) {
    if (q.error instanceof ApiClientError && (q.error.status === 404 || q.error.status === 410)) return null;
    return (
      <p role="status" className="text-sm text-slate-700">
        {vt('prearrival.wallet.error')}
      </p>
    );
  }
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <a
        href={q.data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
      >
        <Wallet aria-hidden="true" className="h-5 w-5" />
        {vt('prearrival.wallet.add')}
      </a>
      <p className="mt-2 text-xs text-slate-600">{vt('prearrival.wallet.hint')}</p>
    </section>
  );
}
